import type * as React from 'react';

import { cn } from '@/lib/utils';

// =============================================================================
// Card Root
// =============================================================================

export type CardPadding = 'none' | 'md';
export type CardAppearance =
  | 'default'
  | 'glass'
  | 'glass-shadow'
  | 'glass-elevated'
  | 'glass-primary'
  | 'brand'
  | 'soft'
  | 'outline';

const cardRootPaddingClasses: Record<CardPadding, string> = {
  none: '',
  md: 'py-6 data-[size=sm]:py-4',
};

const cardContentPaddingClasses: Record<CardPadding, string> = {
  none: '',
  md: 'px-6 group-data-[size=sm]/card:px-4',
};

const cardAppearanceClasses: Record<CardAppearance, string> = {
  default: 'bg-card text-card-foreground ring-foreground/10 shadow-xs ring-1',
  glass:
    'bg-card text-card-foreground border border-border shadow-none ring-0 supports-backdrop-filter:backdrop-blur-[5px]',
  'glass-shadow':
    'bg-card text-card-foreground border border-border shadow-[0_2px_12px_0_#0000000a] ring-0 supports-backdrop-filter:backdrop-blur-[5px]',
  'glass-elevated':
    'bg-card text-card-foreground border border-border shadow-[0_4px_32px_0_#0000001a] ring-0 supports-backdrop-filter:backdrop-blur-[5px]',
  'glass-primary':
    'bg-card text-card-foreground border border-opacity-primary-20 shadow-none ring-0 supports-backdrop-filter:backdrop-blur-[5px]',
  brand: 'bg-primary text-primary-foreground shadow-none ring-0',
  soft: 'bg-opacity-primary-20 text-card-foreground shadow-none ring-0',
  outline:
    'bg-transparent text-card-foreground shadow-none ring-1 ring-primary',
};

export interface CardProps extends React.ComponentProps<'div'> {
  size?: 'default' | 'sm';
  padding?: CardPadding;
  appearance?: CardAppearance;
}

function CardRoot({
  className,
  size = 'default',
  padding = 'md',
  appearance = 'default',
  ...props
}: CardProps) {
  return (
    <div
      data-slot="card"
      data-size={size}
      data-appearance={appearance}
      className={cn(
        'gap-6 overflow-hidden rounded-xl text-sm has-[>img:first-child]:pt-0 data-[size=sm]:gap-4 *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl group/card flex flex-col',
        cardAppearanceClasses[appearance],
        cardRootPaddingClasses[padding],
        className,
      )}
      {...props}
    />
  );
}
CardRoot.displayName = 'Card';

// =============================================================================
// Card Header
// =============================================================================

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'gap-2 rounded-t-xl px-6 group-data-[size=sm]/card:px-4 [.border-b]:pb-6 group-data-[size=sm]/card:[.border-b]:pb-4 group/card-header @container/card-header grid auto-rows-min items-start has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto]',
        className,
      )}
      {...props}
    />
  );
}
CardHeader.displayName = 'Card.Header';

// =============================================================================
// Card Title
// =============================================================================

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-base leading-normal font-semibold group-data-[size=sm]/card:text-sm',
        className,
      )}
      {...props}
    />
  );
}
CardTitle.displayName = 'Card.Title';

// =============================================================================
// Card Description
// =============================================================================

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn(
        'text-muted-foreground text-sm group-data-[appearance=brand]/card:text-primary-foreground/90',
        className,
      )}
      {...props}
    />
  );
}
CardDescription.displayName = 'Card.Description';

// =============================================================================
// Card Action
// =============================================================================

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  );
}
CardAction.displayName = 'Card.Action';

// =============================================================================
// Card Content
// =============================================================================

export interface CardContentProps extends React.ComponentProps<'div'> {
  padding?: CardPadding;
}

function CardContent({
  className,
  padding = 'md',
  ...props
}: CardContentProps) {
  return (
    <div
      data-slot="card-content"
      className={cn(cardContentPaddingClasses[padding], className)}
      {...props}
    />
  );
}
CardContent.displayName = 'Card.Content';

// =============================================================================
// Card Footer
// =============================================================================

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'rounded-b-xl px-6 group-data-[size=sm]/card:px-4 [.border-t]:pt-6 group-data-[size=sm]/card:[.border-t]:pt-4 flex items-center',
        className,
      )}
      {...props}
    />
  );
}
CardFooter.displayName = 'Card.Footer';

// =============================================================================
// Compound Component (Dot Notation)
// =============================================================================

/**
 * Card with composition API.
 * Attach sub-components as static properties for convenient access.
 *
 * @example
 * ```tsx
 * <Card>
 *   <Card.Header>
 *     <Card.Title>Title</Card.Title>
 *     <Card.Description>Description</Card.Description>
 *   </Card.Header>
 *   <Card.Content>Content</Card.Content>
 *   <Card.Footer>Footer</Card.Footer>
 * </Card>
 * ```
 */
const Card = Object.assign(CardRoot, {
  Root: CardRoot,
  Header: CardHeader,
  Title: CardTitle,
  Description: CardDescription,
  Action: CardAction,
  Content: CardContent,
  Footer: CardFooter,
});

// =============================================================================
// Type Exports
// =============================================================================

export type CardHeaderProps = React.ComponentProps<typeof CardHeader>;
export type CardFooterProps = React.ComponentProps<typeof CardFooter>;
export type CardTitleProps = React.ComponentProps<typeof CardTitle>;
export type CardActionProps = React.ComponentProps<typeof CardAction>;
export type CardDescriptionProps = React.ComponentProps<typeof CardDescription>;

// =============================================================================
// Exports
// =============================================================================

export { Card };
